<template>
  <div :data-dusk="dataDusk" :class="{'loading': loading}">
    <div  class="header-wrapper">
      <div class="flex-row align-items-center justify-content-between container-h-xxl">
        <dd-page-header :bread-crumbs="breadCrumbs" v-if="header" >
          {{ header }}
        </dd-page-header>
        <slot name="task-buttons"></slot>
      </div>
      <div v-if="multipleSteps" class="flex-row justify-content-center pt-lg pb-lg">
        <dd-form-indicator
            @go-to-step="goToStep"
            :default-active-step-id="defaultActiveStepId"
            :steps="steps"
            :activeStepIndex="activeStepIndex"/>
      </div>
    </div>

    <div class="flex-row justify-content-center container-v-lg form-page-container-wrapper ">
      <div class="form-page-container">
        <form v-bind="$attrs" ref="form" @submit.prevent="()=>{}">
          <div class="main-form">
            <slot name="steps"></slot>
          </div>
        </form>
      </div>
    </div>
    <transition name="slide">
      <div v-if="serverSideErrorsArray.length && submitted && showErrorSummary" class="error-summary form-page-container-wrapper">
        <div class="form-page-container">
          <div class="flex-row justify-content-between align-items-center">
            <div class="flex-row align-items-center gap-xs">
              <AlertIcon class="fill-white color-white icon-size-xl"/>
              <h2 class="color-white mb-0">Het formulier bevat een aantal fouten</h2>
            </div>
            <div class="">
              <dd-button @click="closeSummary" class="bg-color-transparent color-white">
                <template #icon>
                  <CloseIcon class="icon-size-lg"/>
                </template>
              </dd-button>
            </div>
          </div>
          <ul>
            <li class="font-medium" v-for="(error, index) of serverSideErrorsArray" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
    </transition>
    <div class="action-buttons shadow-xxl" v-if="steps">
      <div class="form-page-container-wrapper">
        <div class="form-page-container flex-row gap-sm justify-content-end">
          <span v-if="savedAt" class="flex-row align-items-center gap-xxs">
            <CheckCircleOutlineIcon class="color-success"/>
            <small class="font-size-sm font-light">Om {{ savedAtFromNow }} opgeslagen</small>
          </span>
          <dd-button data-dusk="previous-step" @click="prevStep"
              v-if="activeStepIndex !== 0 && multipleSteps" type="info">
            terug
          </dd-button>
          <dd-button
              @click="() => {
                onSubmit();
              }"
              data-dusk="submit"
              :loading="loading"
              v-if="!multipleSteps || lastStep || savePerStep"
              type="success"
              :disabled="currentStepInvalid">
            <template #icon>
              <ContentSave class="icon-size-md"/>
            </template>
            opslaan
          </dd-button>
          <dd-button
              data-dusk="next-step"
              @click="nextStep"
              v-if="!lastStep && multipleSteps"
              type="primary"
              :disabled="currentStepInvalid">
            volgende
          </dd-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateFormatsEnum from '../../enums/DateFormatsEnum';
import debounce from 'lodash/throttle';
import ContentSave from 'vue-material-design-icons/ContentSave.vue';
import store from "./store";
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';

export default {
  name: "FormPage",
  emits: ['submit'],
  components: {
    ContentSave,
  },
  beforeCreate() {
    const STORE_KEY = '$_form_page'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
  },
  created() {
    if(this.successMessage) {
      this.setSuccessMessage(this.successMessage);
    }
    if (this.submitRoute) {
      this.getValidationRulesForRouteName(this.submitRoute);
    }
  },
  mounted() {
    let formElement = this.$refs.form;
    this.findStepElements();
    const observer = new MutationObserver(this.findStepElements);
    observer.observe(formElement, {
      childList: true,
      subtree: true,
    });

    this.updateSteps();
    if(this.validOnInit) {
      this.initializeValidSteps();
    }
    this.checkFormStepIsValid();

    if (this.defaultActiveSectionId !== undefined && this.defaultActiveSectionId !== null) {
      this.scrollToDefaultActiveSectionWhenAvailable();
    }
  },
  activated () {
    this.goToStep(0);
  },
  props: {
    successMessage: {
      type: String,
      required: false,
      default: 'Succesvol opgeslagen!'
    },
    submitRoute: {
      type: String,
      required: false,
      default: null,
    },
    savePerStep: {
      type: Boolean,
      required: false,
      default: false,
    },
    validOnInit: {
      type: Boolean,
      required: false,
      default: true,
    },
    header: {
      type: [String, Number],
      required: false,
    },
    throttle: {
      type: Boolean,
      required: false,
      default: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    savedAt: {
      type: Object,
      required: false,
      default: null,
    },
    dataDusk: {
      type: String,
      required: false,
      default: 'form-page',
    },
    breadCrumbs: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      activeStepIndex: 0,
      steps: [],
      validSteps: {},
      submitted: false,
    }
  },
  methods: {
    ...mapActions(`$_form_page`, {
      getValidationRulesForRouteName: 'getValidationRulesForRouteName',
    }),
    ...mapMutations(`$_notifications`, {
      setSuccessMessage: 'SET_SUCCESS_MESSAGE',
    }),
    ...mapMutations(`$_form_page`, {
      resetAxiosError: 'RESET_AXIOS_ERROR',
    }),
    async scrollToDefaultActiveSectionWhenAvailable()  {
      const section = await this._waitForElementWithIdIn(this.defaultActiveSectionId, this.$refs.form);
      if (!section) return;
      let position = section.getBoundingClientRect();
      window.scrollTo(position.left, position.top + window.scrollY - 100);
    },
    _waitForElementWithIdIn(id, parentElement, waitFor = 5000) {
      // eslint-disable-next-line consistent-return
      return new Promise((resolve) => {
        const waitTimer = setTimeout(() => {
          clearTimeout(waitTimer);
          resolve(undefined);
        }, waitFor);

        if (document.getElementById(id)) {
          return resolve(document.getElementById(id));
        }

        const observer = new MutationObserver(() => {
          if (document.getElementById(id)) {
            observer.disconnect();
            resolve(document.getElementById(id));
          }
        });

        observer.observe(parentElement || document.body, {
          childList: true,
          subtree: true,
        });
      });
    },
    findStepElements() {
      this.steps = this.$refs.form.getElementsByClassName('form-step');
    },
    goToStep(stepIndex) {
      // If current step isn't complete yet return.
      if (stepIndex > this.activeStepIndex && this.currentStepInvalid) return;
      this.activeStepIndex = stepIndex;
      this.updateSteps();
    },
    nextStep() {
      if (this.activeStepIndex + 1 >= this.steps.length) return;
      this.activeStepIndex++;
      this.updateSteps();
    },
    prevStep() {
      if (this.activeStepIndex - 1 < 0) return;
      this.activeStepIndex--;
      this.updateSteps();
    },
    updateSteps() {
      for (let step of this.steps) {
        step.classList.remove('active');
        step.classList.add('hidden');
      }
      // The active step
      this.steps[this.activeStepIndex].classList.add('active');
      this.steps[this.activeStepIndex].classList.remove('hidden');
      this.setValidSteps();
    },
    onSubmit: debounce(function() {this.submitForm()}, 1500, {
      leading: true,
      trailing: false,
    }),
    submitForm() {
      this.submitted = true;
      this.resetAxiosError();
      this.$emit('submit', this.$refs.form);
    },
    initializeValidSteps() {
      // Assume all are valid on load
      for (let i = 0; i < this.steps.length; i++) {
        let clone = Object.assign({}, this.validSteps);
        clone[i] = true;
        this.validSteps = clone;
      }
    },
    setValidSteps() {
      if (this.steps[this.activeStepIndex].hasAttribute('invalid')) {
        let clone =  Object.assign({}, this.validSteps);
        delete clone[this.activeStepIndex];
        this.validSteps = clone;
      }
      else {
        this.validSteps = {...this.validSteps, [this.activeStepIndex]: true}
      }
    },
    checkFormStepIsValid() {
      if (!this.steps || this.steps.length === 0) return;
      this.setValidSteps();
      let observer = new MutationObserver(() => this.setValidSteps());
      observer.observe(this.$refs.form, {
        attributes: true,
        attributeFilter: ['invalid'],
        subtree: true
      });
    },
    closeSummary() {
      this.submitted = false;
    },
  },
  watch: {
    successMessage(message) {
      this.setSuccessMessage(message);
    }
  },
  computed: {
    ...mapGetters(`$_form_page`, {
      serverSideErrorsArray: 'serverSideErrorsArray',
    }),
    ...mapState(`$_form_page`, {
      showErrorSummary: 'showErrorSummary',
    }),
    savedAtFromNow() {
      return this.savedAt.format(DateFormatsEnum.DISPLAY_DATETIME);
    },
    currentStepInvalid() {
      return !(this.activeStepIndex in this.validSteps);
    },
    multipleSteps() {
      return this.steps.length > 1;
    },
    lastStep() {
      if (this.activeStepIndex + 1 === this.steps.length) return true;
    },
    defaultActiveStepId() {
      return this.$route?.query?.step;
    },
    defaultActiveSectionId() {
      return this.$route?.query?.section;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/_default.scss";

.hidden {
  display: none;
}
.main-form {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: $spacing-lg;

  .action-buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: $spacing-sm;
  }
}

.header-wrapper {
  background-color: white;
  padding-top: $spacing-xl;
}
.form-page {
  display: flex;
  position: relative;
  gap: $spacing-md;
  background-color: $main-content-bg-color;
  //.content {
  //  flex-grow: 1;
  //  flex-shrink: 0;
  //}
  height: 100%;
  .action-buttons {
    flex-grow: 0;
    flex-shrink: 1;
  }

}
body.has-sidebar {

  .action-buttons {
    left: $sidebar-width * 2;
  }

  &.collapsed-customer-info {
    .action-buttons {
      left: $sidebar-width;
    }
  }
  &.collapsed-sidebar {
    .action-buttons {
      left: $sidebar-collapsed-width + $sidebar-width;
    }

    &.collapsed-customer-info {
      .action-buttons {
        left: $sidebar-collapsed-width;
      }
    }
  }

}

.action-buttons {
  transition: $transition-base;
  position: fixed;
  bottom: 0;
  z-index: 100;
  right: 0;
  left: 0;
  padding: $spacing-md  $spacing-lg;
  display: flex;
  justify-content: flex-end;
  background-color: white;
}


.content {
  width: 50vw;
  height: 100%;
  overflow-y: auto;
}

.form-page-container-wrapper {
  width: 100%;
  display: flex;
  .form-page-container {
    max-width: 820px;
    width: 100%;
    margin: auto;
  }
}

.error-summary {
  position: sticky;
  bottom: 69px;
  max-height: 110vh;
  width: 100%;
  padding: $spacing-lg;
  color: white;
  background-color: $brand-danger;
  box-shadow: $shadow-lg;
}
.slide-enter-active, .slide-leave-active {
  transition: all .6s ease-in-out;
}
.slide-enter, .slide-leave-to /* .fade-leave-active below version 2.1.8 */ {
  max-height: 0;
}
</style>
